import React, { useState, useEffect, useMemo } from 'react';
import { Link, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import clsx from 'clsx';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import { paginationItems, FORMA_LINK_BTN } from '../../utils/listPageUi';
import localStorageService from '../../utils/localStorageService';
import { 
  CertificatesList, 
  CertificateDetails, 
  CertificateForm,
  CertificatePreviewModal
} from '../../components/certifications';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import {
  AcademicCapIcon,
  DocumentPlusIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  DocumentArrowDownIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  PencilIcon,
  ArrowPathIcon,
  XMarkIcon,
  CheckIcon,
  FunnelIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  PrinterIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { downloadCertificatePDF } from '../../utils/pdfService';

// Composant pour visualiser un certificat avec son ID
const CertificateView = () => {
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  
  useEffect(() => {
    // Charger les données du certificat depuis localStorage
    const fetchCertificateData = async () => {
      setLoading(true);
      
      try {
        // Récupérer toutes les certifications
        const certifications = localStorageService.getAllItems('certifications');
        
        // Trouver le certificat par ID
        const found = certifications.find(cert => cert.id === id);
        
        setTimeout(() => {
          if (found) {
            setCertificate(found);
            toast.success(`Visualisation du certificat ${found.id}`);
          } else {
            toast.error("Certificat introuvable");
          }
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error('Erreur lors du chargement du certificat:', error);
        toast.error("Erreur lors du chargement du certificat");
        setLoading(false);
      }
    };
    
    fetchCertificateData();
  }, [id]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-slate-700 dark:bg-slate-900/75">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }
  
  return <CertificatePreview certification={certificate} />;
};

// Composant pour afficher un certificat complet
const CertificatePreview = ({ certification }) => {
  const navigate = useNavigate();

  if (!certification) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
        <div className="flex items-center mb-6">
          <button 
            onClick={() => navigate('/certifications')} 
            className="rounded-xl border border-slate-200 bg-white p-2 text-slate-700 shadow-soft transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900/75 dark:text-slate-200 dark:hover:bg-slate-800/80 mr-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Détails du Certificat</h2>
        </div>
        <div className="flex flex-col items-center justify-center p-12 text-center">
          <AcademicCapIcon className="h-16 w-16 text-slate-900 dark:text-slate-50/30 mb-4" />
          <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-2">Certificat introuvable</h3>
          <p className="text-slate-600 dark:text-slate-400">Impossible de charger les détails de ce certificat.</p>
        </div>
      </div>
    );
  }

  // Fonctions pour gérer les actions
  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    try {
      downloadCertificatePDF(certification);
      toast.success('Téléchargement du certificat démarré');
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      toast.error(`Erreur: ${error.message}`);
    }
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      {/* En-tête avec actions */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/certifications')} 
            className="rounded-xl border border-slate-200 bg-white p-2 text-slate-700 shadow-soft transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900/75 dark:text-slate-200 dark:hover:bg-slate-800/80 mr-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
            {certification.certificateType === 'diploma' ? 'Diplôme' : 
             certification.certificateType === 'certification' ? 'Certification' : 'Attestation'}
            {' '}{certification.id}
          </h2>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handlePrint}
            className="bg-orange-500 text-slate-900 dark:text-slate-50 px-3 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center space-x-1"
          >
            <PrinterIcon className="h-4 w-4" />
            <span>Imprimer</span>
          </button>
          <button
            onClick={handleDownloadPDF}
            className="bg-red-500 text-slate-900 dark:text-slate-50 px-3 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-1"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span>PDF</span>
          </button>
        </div>
      </div>

      {/* Aperçu du certificat */}
      <div className="bg-white text-gray-800 rounded-xl overflow-hidden shadow-2xl print:shadow-none mb-6">
        {/* En-tête du certificat */}
        <div className="bg-indigo-900 text-slate-900 dark:text-slate-50 p-6">
          <div className="flex justify-between">
            <div>
              <h2 className="text-3xl font-bold">
                {certification.certificateType === 'diploma' ? 'DIPLÔME' : 
                 certification.certificateType === 'certification' ? 'CERTIFICATION' : 'ATTESTATION'}
              </h2>
              <p className="mt-1 opacity-70">FORMA PRO EXCELLENCE</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">{certification.id}</div>
              <div className="mt-1 opacity-70">Date: {new Date(certification.issueDate).toLocaleDateString('fr-FR')}</div>
            </div>
          </div>
        </div>
        
        {/* Informations étudiant et formateur */}
        <div className="grid grid-cols-2 gap-10 p-6 border-b">
          <div>
            <h3 className="font-bold text-gray-600 mb-2">Délivré à:</h3>
            <div className="text-lg font-bold">{certification.studentName}</div>
            <div className="text-gray-600 mt-1">ID: {certification.studentId}</div>
          </div>
          <div>
            <h3 className="font-bold text-gray-600 mb-2">Formateur:</h3>
            <div className="text-lg font-bold">{certification.instructorName}</div>
            <div className="text-gray-600 mt-1">ID: {certification.instructorId}</div>
          </div>
        </div>

        {/* Détails du certificat */}
        <div className="p-6">
          <h3 className="text-xl font-bold text-center mb-6">{certification.certificateName}</h3>
          
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="border p-4 rounded-lg">
              <h4 className="font-semibold text-gray-600">Date de délivrance</h4>
              <p className="font-bold">{new Date(certification.issueDate).toLocaleDateString('fr-FR')}</p>
            </div>
            {certification.expirationDate && (
              <div className="border p-4 rounded-lg">
                <h4 className="font-semibold text-gray-600">Date d'expiration</h4>
                <p className="font-bold">{new Date(certification.expirationDate).toLocaleDateString('fr-FR')}</p>
              </div>
            )}
          </div>
          
          {certification.remarks && (
            <div className="border p-4 rounded-lg mb-6">
              <h4 className="font-semibold text-gray-600 mb-2">Remarques</h4>
              <p>{certification.remarks}</p>
            </div>
          )}
          
          {certification.score && (
            <div className="flex justify-center my-6">
              <div className="bg-indigo-50 rounded-full w-28 h-28 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold text-indigo-900">{certification.score}</span>
                <span className="text-sm text-indigo-600">/100</span>
              </div>
            </div>
          )}
        </div>

        {/* Pied de page */}
        <div className="bg-gray-100 p-6 text-center text-gray-600">
          <p>Ce document certifie que l'étudiant a complété avec succès le programme de formation.</p>
          <p className="text-sm mt-2">ID: {certification.id}</p>
        </div>
      </div>

      {/* Statut du certificat */}
      <div className="p-4 bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-lg rounded-xl border border-slate-200 dark:border-slate-600">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-slate-900 dark:text-slate-50 font-medium">Statut:</h3>
            <div className="mt-1">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                certification.status === 'issued' 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {certification.status === 'issued' ? 'Délivré' : 'En attente'}
              </span>
            </div>
          </div>
          <div>
            <h3 className="text-slate-900 dark:text-slate-50 font-medium">Type:</h3>
            <div className="mt-1">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                certification.certificateType === 'diploma' 
                  ? 'bg-forma-500/20 text-forma-700 dark:text-forma-300' 
                  : certification.certificateType === 'certification'
                  ? 'bg-blue-500/20 text-blue-400'
                  : 'bg-green-500/20 text-green-400'
              }`}>
                {certification.certificateType === 'diploma' ? 'Diplôme' : 
                 certification.certificateType === 'certification' ? 'Certification' : 'Attestation'}
              </span>
            </div>
          </div>
          <Link
            to={`/certifications/${certification.id}/edit`}
            className="bg-yellow-500 text-slate-900 dark:text-slate-50 px-3 py-2 rounded-lg hover:bg-yellow-600 transition-colors flex items-center space-x-1"
          >
            <PencilIcon className="h-4 w-4" />
            <span>Modifier</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

const CertificationsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [certsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedCertification, setSelectedCertification] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  // Vérifier si les données ont déjà été initialisées
  const [isDataInitialized, setIsDataInitialized] = useState(() => {
    return localStorage.getItem('certifications_initialized') === 'true';
  });

  // État pour le formulaire d'ajout/modification
  const [formData, setFormData] = useState({
    studentId: '',
    studentName: '',
    certificateType: 'diploma',
    certificateName: '',
    issueDate: '',
    expirationDate: '',
    score: '',
    instructorId: '',
    instructorName: '',
    remarks: ''
  });

  // No mock certifications - using only real certification data

  // Récupération des certifications
  const { data: certificationsData, isLoading } = useQuery('certifications', async () => {
    try {
      // Récupérer les certifications de localStorage (après l'initialisation potentielle)
      const certifications = localStorageService.getAllItems('certifications');
      return { certifications: certifications };
    } catch (error) {
      console.error('Erreur de chargement des certifications:', error);
      return { certifications: [] };
    }
  });

  // Mutation pour ajouter une certification
  const addCertificationMutation = useMutation(
    async (newCertification) => {
      try {
        // Générer un ID unique pour la nouvelle certification
        const certId = `CERT${Date.now().toString().slice(-4)}`;
        
        // Créer l'objet de certification complet
        const certification = {
          id: certId,
          ...newCertification,
          status: newCertification.issueDate ? 'issued' : 'pending'
        };
        
        // Ajouter à localStorage
        localStorageService.addItem('certifications', certification);
        
        return {
          message: 'Certification ajoutée avec succès',
          certification: certification
        };
      } catch (error) {
        console.error('Erreur lors de l\'ajout de la certification:', error);
        throw new Error('Erreur lors de l\'ajout de la certification');
      }
    },
    {
      onSuccess: () => {
        // Rafraîchir les données après ajout
        queryClient.invalidateQueries('certifications');
        setShowAddModal(false);
        resetForm();
      }
    }
  );

  // Mutation pour modifier une certification
  const updateCertificationMutation = useMutation(
    async (updatedCertification) => {
      try {
        // Mettre à jour dans localStorage
        const success = localStorageService.updateItem('certifications', updatedCertification);
        
        if (!success) {
          throw new Error('Certification introuvable');
        }
        
        return {
          message: 'Certification mise à jour avec succès',
          certification: updatedCertification
        };
      } catch (error) {
        console.error('Erreur lors de la mise à jour de la certification:', error);
        throw new Error('Erreur lors de la mise à jour de la certification');
      }
    },
    {
      onSuccess: () => {
        // Rafraîchir les données après modification
        queryClient.invalidateQueries('certifications');
        setShowEditModal(false);
        resetForm();
      }
    }
  );

  // Mutation pour supprimer une certification
  const deleteCertificationMutation = useMutation(
    async (certificationId) => {
      try {
        // Supprimer de localStorage
        const success = localStorageService.deleteItem('certifications', certificationId);
        
        if (!success) {
          throw new Error('Certification introuvable');
        }
        
        return { message: 'Certification supprimée avec succès' };
      } catch (error) {
        console.error('Erreur lors de la suppression de la certification:', error);
        throw new Error('Erreur lors de la suppression de la certification');
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('certifications');
      }
    }
  );

  // Gestion du formulaire
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      studentId: '',
      studentName: '',
      certificateType: 'diploma',
      certificateName: '',
      issueDate: '',
      expirationDate: '',
      score: '',
      instructorId: '',
      instructorName: '',
      remarks: ''
    });
    setSelectedCertification(null);
  };

  // Gérer la soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (showEditModal && selectedCertification) {
      updateCertificationMutation.mutate({
        ...selectedCertification,
        ...formData
      });
    } else {
      addCertificationMutation.mutate(formData);
    }
  };

  // Préparer le formulaire pour l'édition
  const handleEdit = (certification) => {
    setSelectedCertification(certification);
    setFormData({
      studentId: certification.studentId,
      studentName: certification.studentName,
      certificateType: certification.certificateType,
      certificateName: certification.certificateName,
      issueDate: certification.issueDate || '',
      expirationDate: certification.expirationDate || '',
      score: certification.score || '',
      instructorId: certification.instructorId,
      instructorName: certification.instructorName,
      remarks: certification.remarks || ''
    });
    setShowEditModal(true);
    navigate(`/certifications/${certification.id}/edit`);
    toast.success(`Modification du certificat ${certification.id}`);
  };

  // Confirmer la suppression
  const handleDelete = (certificationId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette certification ?')) {
      try {
        // Supprimer de localStorage
        const success = localStorageService.deleteItem('certifications', certificationId);
        
        if (success) {
          // Mise à jour de l'état local
          const updatedCertifications = certificationsData?.certifications.filter(cert => cert.id !== certificationId) || [];
          queryClient.setQueryData('certifications', { certifications: updatedCertifications });
          
          // Afficher un message de succès
          toast.success('Certification supprimée avec succès');
        } else {
          toast.error('Erreur: Certification non trouvée');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression de la certification:', error);
        toast.error('Erreur lors de la suppression de la certification');
      }
    }
  };




  // Gérer la modification
  /* eslint-disable-next-line no-unused-vars */
  const _handleEditDuplicate = (certificate) => {
    console.log('Edit clicked for', certificate.id);
    navigate(`/certifications/${certificate.id}/edit`);
    toast.success(`Modification du certificat ${certificate.id}`);
  };

  const allCertifications = useMemo(() => certificationsData?.certifications ?? [], [certificationsData?.certifications]);

  const formaLinkBtn = FORMA_LINK_BTN;

  const statsStrip = useMemo(
    () => [
      {
        title: 'Total certificats',
        value: String(allCertifications.length),
        hint: 'Tous types confondus',
        icon: DocumentTextIcon,
        iconWrap: 'bg-forma-500/15 text-forma-700 dark:bg-forma-500/20 dark:text-forma-200',
      },
      {
        title: 'Délivrés',
        value: String(allCertifications.filter((c) => c.status === 'issued').length),
        hint: 'Statut délivré',
        icon: CheckCircleIcon,
        iconWrap: 'bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-100',
      },
      {
        title: 'En attente',
        value: String(allCertifications.filter((c) => c.status === 'pending').length),
        hint: 'À compléter ou émettre',
        icon: ClockIcon,
        iconWrap: 'bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-100',
      },
    ],
    [allCertifications]
  );

  const certDistribution = useMemo(() => {
    const total = Math.max(1, allCertifications.length);
    const dip = allCertifications.filter((c) => c.certificateType === 'diploma').length;
    const cert = allCertifications.filter((c) => c.certificateType === 'certification').length;
    const att = allCertifications.filter((c) => c.certificateType === 'attestation').length;
    return [
      {
        name: 'Diplômes',
        count: dip,
        pillClass: 'bg-forma-500/90',
        barClass: 'bg-gradient-to-r from-forma-400 to-forma-700',
        pct: (dip / total) * 100,
      },
      {
        name: 'Certifications',
        count: cert,
        pillClass: 'bg-teal-500/90',
        barClass: 'bg-gradient-to-r from-teal-400 to-emerald-600',
        pct: (cert / total) * 100,
      },
      {
        name: 'Attestations',
        count: att,
        pillClass: 'bg-emerald-500/90',
        barClass: 'bg-gradient-to-r from-emerald-400 to-forma-600',
        pct: (att / total) * 100,
      },
    ];
  }, [allCertifications]);

  // Onglets
  const tabs = [
    { id: 'all', name: 'Tous les Certificats', icon: DocumentTextIcon },
    { id: 'issued', name: 'Délivrés', icon: CheckCircleIcon },
    { id: 'pending', name: 'En Attente', icon: ClockIcon },
    { id: 'diploma', name: 'Diplômes', icon: AcademicCapIcon },
    { id: 'certification', name: 'Certifications', icon: DocumentDuplicateIcon },
    { id: 'attestation', name: 'Attestations', icon: DocumentArrowDownIcon },
  ];

  const tabCounts = useMemo(
    () => ({
      all: allCertifications.length,
      issued: allCertifications.filter((c) => c.status === 'issued').length,
      pending: allCertifications.filter((c) => c.status === 'pending').length,
      diploma: allCertifications.filter((c) => c.certificateType === 'diploma').length,
      certification: allCertifications.filter((c) => c.certificateType === 'certification').length,
      attestation: allCertifications.filter((c) => c.certificateType === 'attestation').length,
    }),
    [allCertifications]
  );

  // Filtrer les certifications
  const filteredCertifications = certificationsData?.certifications
    ? certificationsData.certifications.filter(cert => {
        const matchesSearch = 
          cert.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cert.certificateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cert.instructorName.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesType = filterType === 'all' || cert.certificateType === filterType;
        // Filter by tab
        if (activeTab === 'all') return matchesSearch && matchesType;
        if (activeTab === 'issued') return matchesSearch && matchesType && cert.status === 'issued';
        if (activeTab === 'pending') return matchesSearch && matchesType && cert.status === 'pending';
        if (['diploma', 'certification', 'attestation'].includes(activeTab)) {
          return matchesSearch && matchesType && cert.certificateType === activeTab;
        }
        
        return matchesSearch && matchesType;
      })
    : [];

  const paginatedCertifications = filteredCertifications.slice((currentPage - 1) * certsPerPage, currentPage * certsPerPage);
  const totalCertifications = filteredCertifications.length;
  const totalPages = Math.max(1, Math.ceil(totalCertifications / certsPerPage));
  const pageNav = paginationItems(currentPage, totalPages);
  const pageSliceLabel =
    totalCertifications === 0
      ? 'Aucun certificat'
      : `Affichage ${(currentPage - 1) * certsPerPage + 1}–${Math.min(currentPage * certsPerPage, totalCertifications)} sur ${totalCertifications}`;

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, filterType]);

  /* eslint-disable-next-line no-unused-vars */
  const _handleDeleteDuplicate = async (certId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce certificat ?')) {
      try {
        // Logique de suppression simulée
        console.log(`Suppression du certificat ${certId}`);
        toast.success('Certificat supprimé avec succès');
      } catch (error) {
        toast.error('Erreur lors de la suppression');
        console.error(error);
      }
    }
  };

  return (
    <Routes>
      <Route path="/" element={
        <div className="space-y-6">
          <PageHeader
            title="Certifications"
            description="Délivrance et suivi des diplômes, certifications et attestations"
            actions={
              <button type="button" onClick={() => setShowAddModal(true)} className={formaLinkBtn}>
                <DocumentPlusIcon className="h-5 w-5" />
                <span>Nouveau certificat</span>
              </button>
            }
          />

          <UiCard padding={false} className="overflow-hidden">
            <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
              <div className="grid gap-6 p-6 sm:grid-cols-3 sm:gap-5">
                {statsStrip.map((stat, index) => (
                  <motion.div
                    key={stat.title}
                    layout
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.06 }}
                    whileHover={{ y: -3 }}
                    className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
                  >
                    <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                      <div
                        aria-hidden
                        className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                      />
                      <div className="relative flex gap-4">
                        <div
                          className={clsx(
                            'relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                            stat.iconWrap
                          )}
                        >
                          <stat.icon className="h-6 w-6" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                            {stat.title}
                          </p>
                          <p className="mt-0.5 text-3xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white">
                            {stat.value}
                          </p>
                          <p className="mt-1 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                            {stat.hint}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="border-b border-slate-200 bg-white/40 px-4 py-4 dark:border-slate-700 dark:bg-slate-900/35">
              <div className="flex gap-2 overflow-x-auto pb-1 hide-scrollbar">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setActiveTab(tab.id);
                      setCurrentPage(1);
                    }}
                    className={clsx(
                      'inline-flex shrink-0 items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-all',
                      activeTab === tab.id
                        ? 'scale-[1.02] bg-forma-600 text-white shadow-md shadow-forma-600/25'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                    )}
                  >
                    <tab.icon className="h-4 w-4" />
                    <span className="max-w-[14rem] truncate">{tab.name}</span>
                    <span
                      className={clsx(
                        'ml-0.5 min-w-[1.75rem] rounded-full px-2 py-0.5 text-center text-xs font-semibold tabular-nums',
                        activeTab === tab.id
                          ? 'bg-white/20 text-white'
                          : 'bg-white/70 text-slate-800 dark:bg-slate-950 dark:text-slate-200'
                      )}
                    >
                      {tabCounts[tab.id] ?? 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </UiCard>

          <UiCard padding={false} className="overflow-hidden">
            <div className="border-b border-slate-200 px-6 py-5 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                Répartition par type
              </h3>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Vue synthétique du portefeuille</p>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {certDistribution.map((item) => (
                  <div
                    key={item.name}
                    className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-600 dark:bg-slate-800/40"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="font-medium text-slate-900 dark:text-slate-50">{item.name}</h4>
                      <div
                        className={clsx(
                          'rounded-full px-2 py-0.5 text-sm font-semibold text-white shadow-sm',
                          item.pillClass
                        )}
                      >
                        {item.count}
                      </div>
                    </div>
                    <div className="h-3 w-full rounded-full bg-slate-200/90 dark:bg-slate-800/80">
                      <div className={clsx('h-3 rounded-full', item.barClass)} style={{ width: `${item.pct.toFixed(1)}%` }} />
                    </div>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                      {item.pct.toFixed(1)}% des certificats
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-b border-slate-200 bg-white/30 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/25">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="relative w-full md:max-w-md">
                  <label htmlFor="cert-search" className="sr-only">
                    Rechercher un certificat
                  </label>
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    id="cert-search"
                    type="search"
                    autoComplete="off"
                    placeholder="Étudiant, titre ou formateur…"
                    value={searchTerm}
                    onChange={handleSearch}
                    className={clsx(
                      'w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500',
                      searchTerm.trim() ? 'pr-10' : 'pr-4'
                    )}
                  />
                  {searchTerm.trim() !== '' ? (
                    <button
                      type="button"
                      aria-label="Effacer la recherche"
                      onClick={() => {
                        setSearchTerm('');
                        setCurrentPage(1);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <FunnelIcon className="h-5 w-5 shrink-0 text-slate-500 dark:text-slate-400" />
                  <select
                    value={filterType}
                    onChange={(e) => {
                      setFilterType(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 md:w-auto"
                  >
                    <option value="all">Tous les types</option>
                    <option value="diploma">Diplômes</option>
                    <option value="certification">Certifications</option>
                    <option value="attestation">Attestations</option>
                  </select>
                </div>
              </div>
            </div>

            {(searchTerm.trim() !== '' || filterType !== 'all') && (
              <div className="flex flex-wrap items-center gap-2 border-b border-forma-200/60 bg-forma-50/50 px-5 py-2.5 dark:border-forma-900/35 dark:bg-forma-950/25">
                <span className="text-[11px] font-bold uppercase tracking-wider text-forma-800 dark:text-forma-400">
                  Filtres actifs
                </span>
                {searchTerm.trim() !== '' ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                    « {searchTerm.trim()} »
                  </span>
                ) : null}
                {filterType !== 'all' ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                    Type : {filterType}
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterType('all');
                    setCurrentPage(1);
                  }}
                  className="ml-auto rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Réinitialiser
                </button>
              </div>
            )}
          </UiCard>

          <UiCard padding={false} className="overflow-hidden">
            {paginatedCertifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-5 px-6 py-16 text-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60">
                  <DocumentTextIcon className="h-10 w-10 text-forma-500/70 dark:text-forma-400/80" />
                </div>
                <div className="max-w-md space-y-2">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Aucun certificat trouvé</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Aucun résultat ne correspond à vos critères de recherche.
                  </p>
                </div>
                <button type="button" onClick={() => setShowAddModal(true)} className={formaLinkBtn}>
                  <DocumentPlusIcon className="h-5 w-5" />
                  <span>Créer un certificat</span>
                </button>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-1 border-b border-slate-200 bg-white/50 px-5 py-3 dark:border-slate-700 dark:bg-slate-900/40 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Liste des certificats</p>
                  <p className="text-xs tabular-nums text-slate-500 dark:text-slate-400">{pageSliceLabel}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[860px]">
                    <thead className="sticky top-0 z-10 bg-slate-100/95 backdrop-blur dark:bg-slate-800/95">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Étudiant
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Titre
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Statut
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {paginatedCertifications.map((cert, rowIdx) => (
                        <motion.tr
                          key={cert.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className={clsx(
                            'transition-colors',
                            rowIdx % 2 === 0
                              ? 'bg-white/90 dark:bg-slate-950/35'
                              : 'bg-slate-50/80 dark:bg-slate-900/45',
                            'hover:bg-forma-50/80 dark:hover:bg-forma-950/25'
                          )}
                        >
                          <td className="whitespace-nowrap px-4 py-3 font-mono text-sm text-slate-900 dark:text-slate-50">
                            {cert.id}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-900 dark:text-slate-50">
                            {cert.studentName}
                          </td>
                          <td className="max-w-xs truncate px-4 py-3 text-slate-700 dark:text-slate-300">
                            {cert.certificateName}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-700 dark:text-slate-300">
                            {cert.issueDate ? new Date(cert.issueDate).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <span
                              className={clsx(
                                'rounded-full px-2.5 py-1 text-xs font-medium',
                                cert.certificateType === 'diploma' &&
                                  'bg-forma-500/15 text-forma-800 dark:text-forma-200',
                                cert.certificateType === 'certification' &&
                                  'bg-teal-500/15 text-teal-900 dark:text-teal-100',
                                cert.certificateType === 'attestation' &&
                                  'bg-emerald-500/15 text-emerald-900 dark:text-emerald-100'
                              )}
                            >
                              {cert.certificateType === 'diploma'
                                ? 'Diplôme'
                                : cert.certificateType === 'certification'
                                  ? 'Certification'
                                  : 'Attestation'}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <span
                              className={clsx(
                                'rounded-full px-2.5 py-1 text-xs font-medium',
                                cert.status === 'issued'
                                  ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200'
                                  : 'bg-amber-500/15 text-amber-900 dark:text-amber-100'
                              )}
                            >
                              {cert.status === 'issued' ? 'Délivré' : 'En attente'}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedCertification(cert);
                                  setShowPreviewModal(true);
                                }}
                                className="rounded-lg bg-forma-500/15 p-2 text-forma-800 transition hover:bg-forma-500/25 dark:text-forma-200"
                                title="Aperçu / PDF"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEdit(cert)}
                                className="rounded-lg bg-amber-500/15 p-2 text-amber-900 transition hover:bg-amber-500/25 dark:text-amber-100"
                                title="Modifier"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(cert.id)}
                                className="rounded-lg bg-red-500/15 p-2 text-red-700 transition hover:bg-red-500/25 dark:text-red-300"
                                title="Supprimer"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalCertifications > certsPerPage ? (
                  <div className="flex flex-col gap-3 border-t border-slate-200 px-5 py-4 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-slate-600 dark:text-slate-400">{pageSliceLabel}</p>
                    <nav className="flex flex-wrap items-center justify-end gap-2" aria-label="Pagination">
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        <ChevronLeftIcon className="h-4 w-4" aria-hidden />
                        Précédent
                      </button>
                      <div className="flex flex-wrap items-center gap-1">
                        {pageNav.map((node) =>
                          node.type === 'ellipsis' ? (
                            <span key={node.key} className="px-2 py-1 text-sm font-medium text-slate-400" aria-hidden>
                              …
                            </span>
                          ) : (
                            <button
                              key={node.page}
                              type="button"
                              onClick={() => setCurrentPage(node.page)}
                              className={clsx(
                                'min-w-[2.25rem] rounded-lg px-2 py-1 text-sm font-semibold transition',
                                currentPage === node.page
                                  ? 'bg-forma-600 text-white shadow-sm'
                                  : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
                              )}
                            >
                              {node.page}
                            </button>
                          )
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        Suivant
                        <ChevronRightIcon className="h-4 w-4" aria-hidden />
                      </button>
                    </nav>
                  </div>
                ) : null}
              </>
            )}
          </UiCard>
      
      {/* Modal d'ajout de certification */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm dark:bg-black/75 z-50 flex items-start justify-center overflow-y-auto">
          <div className="w-full max-w-3xl p-4 pt-8 pb-8">
            <CertificateForm 
              onClose={() => setShowAddModal(false)}
              onSuccess={() => {
                setShowAddModal(false);
                queryClient.invalidateQueries('certifications');
                    toast.success('Certificat ajouté avec succès');
              }}
            />
          </div>
        </div>
      )}
      
      {/* Modal de prévisualisation et téléchargement */}
      {showPreviewModal && selectedCertification && (
        <CertificatePreviewModal 
          certification={selectedCertification} 
          onClose={() => setShowPreviewModal(false)} 
        />
      )}
        </div>
      } />
      {/* Routes pour la création et l'édition */}
      <Route path="/new" element={<div>Formulaire de création</div>} />
      <Route path="/:id" element={<CertificateView />} />
      <Route path="/:id/edit" element={
        <div className="p-6">
          <div className="flex items-center mb-6">
            <button 
              onClick={() => navigate('/certifications')} 
              className="rounded-xl border border-slate-200 bg-white p-2 text-slate-700 shadow-soft transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900/75 dark:text-slate-200 dark:hover:bg-slate-800/80 mr-4"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Modification du Certificat</h2>
          </div>
      {showEditModal && selectedCertification && (
          <CertificateForm 
            initialData={selectedCertification}
            isEditing={true}
              onClose={() => navigate('/certifications')}
            onSuccess={() => {
              queryClient.invalidateQueries('certifications');
                toast.success('Certificat modifié avec succès');
                navigate('/certifications');
            }}
          />
          )}
        </div>
      } />
    </Routes>
  );
};

export default CertificationsPage;